import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

// ---------------------------------------------------------------------------
// Types (client-safe)
// ---------------------------------------------------------------------------
export type SectionKey = "essentials" | "outdoor" | "highlights_events";
export type HubStatus = "draft" | "pending" | "published" | "rejected";

export type HubBranding = {
  id: string;
  city_id: string;
  banner_url: string | null;
  logo_url: string | null;
  brand_color: string | null;
  tagline: string | null;
  seo_title: string | null;
  seo_description: string | null;
  status: HubStatus;
  submitted_at: string | null;
  published_at: string | null;
  review_note: string | null;
};

export type HubSection = {
  id: string;
  city_id: string;
  section_key: SectionKey;
  title: string;
  subtitle: string | null;
  sort_order: number;
};

export type HubEntry = {
  id: string;
  section_id: string;
  city_id: string;
  kind: "curated" | "custom";
  place_id: string | null;
  imported_event_id: string | null;
  curator_note: string | null;
  title: string | null;
  description: string | null;
  url: string | null;
  address: string | null;
  image_url: string | null;
  icon: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  opening_hours: Record<string, any> | null;
  status: HubStatus;
  sort_order: number;
  review_note: string | null;
  submitted_at: string | null;
  published_at: string | null;
};

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
const cityIdInput = z.object({ cityId: z.string().uuid() });

async function assertEditor(supabase: any, userId: string, cityId: string) {
  // Admins bypass; editors must be listed in city_editors.
  const { data: adminRow } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .in("role", ["admin", "super_admin"])
    .limit(1);
  if (adminRow && adminRow.length > 0) return;

  const { data, error } = await supabase
    .from("city_editors")
    .select("city_id")
    .eq("user_id", userId)
    .eq("city_id", cityId)
    .maybeSingle();
  if (error || !data) throw new Error("Forbidden: not an editor for this city");
}

async function assertAdmin(supabase: any, userId: string) {
  const { assertIsAdmin } = await import("@/lib/assert-admin.server");
  await assertIsAdmin(supabase, userId);
}

// ---------------------------------------------------------------------------
// EDITOR API
// ---------------------------------------------------------------------------
export const getMyCityHub = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => cityIdInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertEditor(context.supabase, context.userId, data.cityId);

    const [city, branding, sections, entries] = await Promise.all([
      supabaseAdmin.from("cities").select("id, name, slug").eq("id", data.cityId).maybeSingle(),
      supabaseAdmin.from("city_hub_branding").select("*").eq("city_id", data.cityId).maybeSingle(),
      supabaseAdmin.from("city_hub_sections").select("*").eq("city_id", data.cityId).order("sort_order"),
      supabaseAdmin
        .from("city_hub_entries")
        .select("*")
        .eq("city_id", data.cityId)
        .order("sort_order", { ascending: true }),
    ]);

    return {
      city: city.data,
      branding: (branding.data ?? null) as HubBranding | null,
      sections: (sections.data ?? []) as HubSection[],
      entries: (entries.data ?? []) as HubEntry[],
    };
  });

export const upsertCityBranding = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      cityId: z.string().uuid(),
      patch: z.object({
        banner_url: z.string().max(2000).nullable().optional(),
        logo_url: z.string().max(2000).nullable().optional(),
        brand_color: z.string().max(30).nullable().optional(),
        tagline: z.string().max(500).nullable().optional(),
        seo_title: z.string().max(160).nullable().optional(),
        seo_description: z.string().max(320).nullable().optional(),
      }),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertEditor(context.supabase, context.userId, data.cityId);
    // Editor edits move branding back into 'pending'.
    const patch = {
      ...data.patch,
      status: "pending" as const,
      submitted_at: new Date().toISOString(),
    };
    const { error, data: row } = await supabaseAdmin
      .from("city_hub_branding")
      .upsert({ city_id: data.cityId, ...patch }, { onConflict: "city_id" })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return row as HubBranding;
  });

const entryPatchSchema = z.object({
  kind: z.enum(["curated", "custom"]).optional(),
  place_id: z.string().uuid().nullable().optional(),
  imported_event_id: z.string().uuid().nullable().optional(),
  curator_note: z.string().max(1000).nullable().optional(),
  title: z.string().max(200).nullable().optional(),
  description: z.string().max(2000).nullable().optional(),
  url: z.string().max(2000).nullable().optional(),
  address: z.string().max(500).nullable().optional(),
  image_url: z.string().max(2000).nullable().optional(),
  icon: z.string().max(60).nullable().optional(),
  opening_hours: z.record(z.string(), z.any()).nullable().optional(),
  sort_order: z.number().int().optional(),
});

export const upsertCityHubEntry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      entryId: z.string().uuid().nullable(),
      sectionId: z.string().uuid(),
      cityId: z.string().uuid(),
      kind: z.enum(["curated", "custom"]),
      patch: entryPatchSchema,
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertEditor(context.supabase, context.userId, data.cityId);

    if (data.entryId) {
      const { data: row, error } = await supabaseAdmin
        .from("city_hub_entries")
        .update({ ...data.patch, kind: data.kind, status: "draft" })
        .eq("id", data.entryId)
        .eq("city_id", data.cityId)
        .select("*")
        .single();
      if (error) throw new Error(error.message);
      return row as HubEntry;
    }

    const { data: row, error } = await supabaseAdmin
      .from("city_hub_entries")
      .insert({
        section_id: data.sectionId,
        city_id: data.cityId,
        kind: data.kind,
        status: "draft",
        created_by: context.userId,
        ...data.patch,
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return row as HubEntry;
  });

export const deleteCityHubEntry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ entryId: z.string().uuid(), cityId: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertEditor(context.supabase, context.userId, data.cityId);
    const { error } = await supabaseAdmin
      .from("city_hub_entries")
      .delete()
      .eq("id", data.entryId)
      .eq("city_id", data.cityId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const submitCityHubForReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => cityIdInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertEditor(context.supabase, context.userId, data.cityId);
    const now = new Date().toISOString();

    const [entriesRes, brandingRes] = await Promise.all([
      supabaseAdmin
        .from("city_hub_entries")
        .update({ status: "pending", submitted_at: now })
        .eq("city_id", data.cityId)
        .eq("status", "draft")
        .select("id"),
      supabaseAdmin
        .from("city_hub_branding")
        .update({ submitted_at: now })
        .eq("city_id", data.cityId)
        .eq("status", "pending")
        .select("id"),
    ]);

    const pendingCount = (entriesRes.data?.length ?? 0) + (brandingRes.data?.length ?? 0);

    // Notify admins.
    if (pendingCount > 0) {
      const { data: admins } = await supabaseAdmin
        .from("user_roles")
        .select("user_id")
        .in("role", ["admin", "super_admin"]);
      const { data: city } = await supabaseAdmin
        .from("cities").select("name").eq("id", data.cityId).maybeSingle();
      const cityName = (city as { name?: string } | null)?.name ?? "A city";
      const rows = (admins ?? []).map((a: { user_id: string }) => ({
        user_id: a.user_id,
        title: "City Hub review",
        body: `${cityName} submitted ${pendingCount} hub change${pendingCount === 1 ? "" : "s"} for review.`,
        link: `/admin/city-hub-queue?city=${data.cityId}`,
        kind: "city_hub_review",
      }));
      if (rows.length > 0) {
        await supabaseAdmin.from("notifications").insert(rows);
      }
    }

    return { ok: true, submitted: pendingCount };
  });

// ---------------------------------------------------------------------------
// ADMIN API
// ---------------------------------------------------------------------------
export const listCityHubPendingReviews = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const [entries, branding] = await Promise.all([
      supabaseAdmin
        .from("city_hub_entries")
        .select("*, cities:city_id(name, slug), city_hub_sections:section_id(section_key, title)")
        .eq("status", "pending")
        .order("submitted_at", { ascending: true }),
      supabaseAdmin
        .from("city_hub_branding")
        .select("*, cities:city_id(name, slug)")
        .eq("status", "pending")
        .order("submitted_at", { ascending: true }),
    ]);
    return {
      entries: entries.data ?? [],
      branding: branding.data ?? [],
    };
  });

export const reviewCityHubEntries = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      entryIds: z.array(z.string().uuid()).min(1).max(200),
      action: z.enum(["approve", "reject"]),
      note: z.string().max(1000).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const now = new Date().toISOString();
    const patch =
      data.action === "approve"
        ? { status: "published" as const, published_at: now, reviewed_by: context.userId, review_note: null }
        : { status: "rejected" as const, reviewed_by: context.userId, review_note: data.note ?? null };
    const { error } = await supabaseAdmin
      .from("city_hub_entries")
      .update(patch)
      .in("id", data.entryIds);
    if (error) throw new Error(error.message);
    return { ok: true, count: data.entryIds.length };
  });

export const reviewCityHubBranding = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      cityId: z.string().uuid(),
      action: z.enum(["approve", "reject"]),
      note: z.string().max(1000).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const now = new Date().toISOString();
    const patch =
      data.action === "approve"
        ? { status: "published" as const, published_at: now, review_note: null }
        : { status: "rejected" as const, review_note: data.note ?? null };
    const { error } = await supabaseAdmin
      .from("city_hub_branding")
      .update(patch)
      .eq("city_id", data.cityId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------------------------------------------------------------------------
// PUBLIC API (SSR-safe)
// ---------------------------------------------------------------------------
export type PublicHubEntry = HubEntry & {
  place?: { id: string; name: string; slug: string; image_url: string | null; category: string | null } | null;
  event?: { id: string; title: string | null; start_date: string | null; image_url: string | null } | null;
};

export type PublicCityHub = {
  city: { id: string; name: string; slug: string } | null;
  branding: HubBranding | null;
  sections: HubSection[];
  entries: PublicHubEntry[];
} | null;

export const getPublicCityHub = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ slug: z.string().min(1).max(200) }).parse(d))
  .handler(async ({ data }): Promise<PublicCityHub> => {
    const { data: city } = await supabaseAdmin
      .from("cities")
      .select("id, name, slug, active")
      .eq("slug", data.slug)
      .eq("active", true)
      .maybeSingle();
    if (!city) return null;

    const cityRow = city as { id: string; name: string; slug: string };

    const [brandingRes, sectionsRes, entriesRes] = await Promise.all([
      supabaseAdmin
        .from("city_hub_branding")
        .select("*")
        .eq("city_id", cityRow.id)
        .eq("status", "published")
        .maybeSingle(),
      supabaseAdmin
        .from("city_hub_sections")
        .select("*")
        .eq("city_id", cityRow.id)
        .order("sort_order"),
      supabaseAdmin
        .from("city_hub_entries")
        .select("*")
        .eq("city_id", cityRow.id)
        .eq("status", "published")
        .order("sort_order"),
    ]);

    const entries = (entriesRes.data ?? []) as HubEntry[];

    // Hydrate curated references (place / event) in a single batch.
    const placeIds = entries.map((e) => e.place_id).filter((v): v is string => !!v);
    const eventIds = entries.map((e) => e.imported_event_id).filter((v): v is string => !!v);

    const [placesRes, eventsRes] = await Promise.all([
      placeIds.length
        ? supabaseAdmin
            .from("places")
            .select("id, name, slug, image_url, category")
            .in("id", placeIds)
        : Promise.resolve({ data: [] as Array<Record<string, unknown>> }),
      eventIds.length
        ? supabaseAdmin
            .from("imported_events")
            .select("id, title, start_date, image_url")
            .in("id", eventIds)
        : Promise.resolve({ data: [] as Array<Record<string, unknown>> }),
    ]);

    const placeMap = new Map(((placesRes.data ?? []) as Array<{ id: string }>).map((r) => [r.id, r as any]));
    const eventMap = new Map(((eventsRes.data ?? []) as Array<{ id: string }>).map((r) => [r.id, r as any]));

    const hydrated: PublicHubEntry[] = entries.map((e) => ({
      ...e,
      place: e.place_id ? (placeMap.get(e.place_id) as PublicHubEntry["place"]) ?? null : null,
      event: e.imported_event_id ? (eventMap.get(e.imported_event_id) as PublicHubEntry["event"]) ?? null : null,
    }));

    return {
      city: cityRow,
      branding: (brandingRes.data ?? null) as HubBranding | null,
      sections: (sectionsRes.data ?? []) as HubSection[],
      entries: hydrated,
    };
  });
